import React, { useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { trackEvent } from "./tracker";

// Listener teardown for the interaction tracking below, keyed off the node so
// a re-registered ref never stacks a second pair of listeners on the same
// input. WeakMap rather than a property on the element: nothing of ours ends
// up visible on the DOM node.
const wired = new WeakMap();

/* The state of the screen at the instant of a refusal.

   Geometry and interaction history only. No typed value has ever left this
   module and none starts here: every field below is a number, a boolean, or
   null.

   Each answers a question that `value_length: 0` on its own cannot:

     field_top / viewport_h  was the field they are being told about even on
                             screen, or are they looking at a button and a
                             message about something they cannot see?
     keyboard_up             visualViewport shrinks when the on-screen keyboard
                             is raised. False while `ever_focused` is true is a
                             field that took the cursor and gave no keyboard,
                             which is a WKWebView failure we cannot reproduce
                             from a desktop and cannot fix blind.
     ever_focused            did they ever get into the field at all.
     ever_typed              did a DOM `input` event ever land on it. True with
                             value_length 0 means something wiped what they
                             typed, and that is our bug, not their omission.
     attempt                 which press this is. The sixth press is a
                             different story from the first and they are
                             currently indistinguishable. */
function describeMoment(field, el, seen) {
  const out = {
    attempt: (seen.attempts += 1),
    secs_on_form: Math.round((Date.now() - seen.since) / 1000),
    ever_focused: !!seen.focused[field],
    ever_typed: !!seen.typed[field],
    any_field_typed: !!seen.anyTyped,
  };
  try {
    const layoutH = window.innerHeight;
    const visualH = window.visualViewport ? Math.round(window.visualViewport.height) : layoutH;
    const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    out.viewport_h = visualH;
    out.scroll_y = Math.round(window.scrollY || 0);
    // 120px is well above browser-chrome jitter and well below any phone
    // keyboard, so this reads as "a keyboard is up" and nothing else.
    out.keyboard_up = window.visualViewport ? layoutH - visualH > 120 : null;
    out.field_top = rect ? Math.round(rect.top) : null;
    out.field_visible = rect ? rect.top >= 0 && rect.bottom <= visualH : null;
  } catch {
    /* A refusal must still be recorded on a browser that has none of this. */
  }
  return out;
}

/* Field-level validation errors for the signup forms.

   Why this exists rather than another toast.

   A visitor in the iPhone Facebook browser tapped "Create my account" three
   times and never once reached the network: client-side validation rejected
   every attempt, and the only thing saying so was a toast. react-hot-toast
   pins to the top of the viewport with position:fixed, and iOS webviews
   routinely render fixed elements off-screen while the keyboard is up, which
   is exactly the state a form is in when it fires one. From where they sat,
   the primary button did nothing, three times, and they left.

   Four fifths of signup traffic is inside Meta's in-app browser, so this is
   the normal case, not an edge one. The message has to live on the field it
   is about, where nothing can cover it.

   It also closes a measurement hole. Every one of these forms fired
   `signup_submitted` only after validation passed, so a visitor blocked at
   this step was indistinguishable in the funnel from one who wandered off:
   both are a `signup_started` with nothing after it. `signup_validation_blocked`
   makes the difference visible, and names the field. */

export function useFieldErrors(context = {}) {
  const [errors, setErrors] = useState({});
  const refs = useRef({});

  /* What the person did before we refused them.

     `value_length` told us the field was empty and that was as far as it went.
     Empty has at least four causes and they need opposite fixes: they never
     saw the field, they saw it and never tapped it, they tapped it and no
     keyboard came up, or they typed and something wiped it. One visitor tapped
     the button six times in 23 seconds against a field that stayed at zero
     characters throughout, and every explanation for that was a guess, because
     the numbers that would settle it were not being collected. These are. */
  const seen = useRef({ focused: {}, typed: {}, anyTyped: false, attempts: 0, since: Date.now() });

  // Pass to a field as ref={register("email")}.
  const register = (field) => (el) => {
    const prev = refs.current[field];
    if (prev && prev !== el && wired.has(prev)) {
      wired.get(prev)();
      wired.delete(prev);
    }
    refs.current[field] = el;
    if (!el || wired.has(el)) return;
    // Native listeners, not React's onChange: an autofill that never fires a
    // React change event still fires a DOM `input`, and that difference is
    // exactly one of the cases being told apart here.
    const onFocus = () => { seen.current.focused[field] = true; };
    const onInput = () => { seen.current.typed[field] = true; seen.current.anyTyped = true; };
    el.addEventListener("focus", onFocus);
    el.addEventListener("input", onInput);
    wired.set(el, () => {
      el.removeEventListener("focus", onFocus);
      el.removeEventListener("input", onInput);
    });
  };

  /* Reject one field: mark it, say why on the field itself, and put the
     cursor in it. Focus alone is not enough on a phone — the field can sit
     under the keyboard — so scroll it to the middle of what's left of the
     viewport first. The toast stays too; it costs nothing and covers the case
     where the field is off-screen. Returns undefined so callers can
     `return fail(...)` straight out of a handler. */
  /* The visible half, with no telemetry attached: mark the field, say why on
     it, and put the cursor there. Callers that already record their own reason
     — a server rejection, say — use this so one refusal is not counted twice
     under two different event names. */
  /* Several fields at once.
     showFieldError REPLACES the error map, by design -- one refusal at a time
     is right when the server names a field. It is wrong for the ordinary
     "you pressed submit on an empty form" case, where every required box
     should be marked: three calls to it would leave only the last one
     showing. */
  const showFieldErrors = (map) => setErrors({ ...map });

  const showFieldError = (field, message) => {
    setErrors({ [field]: message });
    toast.error(message);
    const node = refs.current[field];
    if (node) {
      try {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        /* older webviews: focus still moves */
      }
      try {
        node.focus({ preventScroll: true });
      } catch {
        node.focus();
      }
    }
    return undefined;
  };

  const fail = (field, message, meta = {}) => {
    setErrors({ [field]: message });
    toast.error(message);
    /* How many characters were actually sitting in the field, never what they
       were — no typed value ever leaves the browser. This one number separates
       the two failures that look identical in the funnel: an empty field
       (they never filled it, or a stray tap submitted early) from a full one
       (we rejected something they can see, which is our bug, not theirs).
       Not having it is why the last round of blocked signups took a day to
       explain. */
    const el = refs.current[field];
    const valueLength = el && typeof el.value === "string" ? el.value.length : null;
    trackEvent("signup_validation_blocked", {
      field, value_length: valueLength,
      ...describeMoment(field, el, seen.current),
      ...context, ...meta,
    });

    if (el) {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        /* older webviews: focus still moves */
      }
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
    return undefined;
  };

  // Typing is the correction, so the error clears the moment they start.
  const clear = (field) => setErrors((e) => (e[field] ? { ...e, [field]: "" } : e));

  const reset = () => setErrors({});

  /* What the field ACTUALLY contains, which is not always what React thinks.

     Autofill is why. iOS AutoFill, Android autofill, and password managers
     write straight into the input element; the synthetic input event React
     listens for does not reliably follow, and it is least reliable inside a
     WKWebView — which is exactly what Meta's in-app browser is, and where
     roughly four fifths of this traffic lives.

     The result is brutal and invisible from our side: the field visibly holds
     their email, React state still holds "", and we tell them to enter a valid
     email while they are looking at one. There is nothing they can do to
     satisfy it, so they tap again, and again. Reading the DOM as a fallback
     makes the value we validate the value they can see. */
  const readField = (field, stateValue) => {
    const fromState = (stateValue ?? "").trim();
    if (fromState) return fromState;
    const el = refs.current[field];
    const fromDom = el && typeof el.value === "string" ? el.value : "";
    return fromDom.trim();
  };

  /* The same autofill recovery, without the trim.

     readField trims because every field it was written for — name, email,
     company — is one where a stray leading space is a typo. A password is the
     opposite: the spaces are part of it, and quietly dropping them would set
     an account's password to something other than what the person typed, then
     lock them out of it at the login screen that doesn't trim. */
  const readFieldRaw = (field, stateValue) => {
    if (stateValue) return stateValue;
    const el = refs.current[field];
    return el && typeof el.value === "string" ? el.value : "";
  };

  // Everything a field needs to be wired up correctly, in one spread.
  const fieldProps = (field) => ({
    ref: register(field),
    "aria-invalid": !!errors[field],
    "aria-describedby": errors[field] ? `${field}-err` : undefined,
    style: errors[field] ? { borderColor: ERROR_BORDER } : undefined,
  });

  // `refs` is exposed so useAutofillAdoption can watch the same nodes these
  // errors point at. Reading the DOM at submit is not enough on its own — see
  // the note in lib/autofillAdoption.
  return { errors, register, fail, showFieldError, showFieldErrors, clear, reset, fieldProps, readField, readFieldRaw, refs };
}

// Tailwind can't be relied on to win a border-colour fight with a field's base
// class, and which one wins is a build detail. An inline style always does.
export const ERROR_BORDER = "#DC2626";

/* aria-live so a screen reader announces it on the tap that reveals it.
   Renders nothing without a message, so it can sit under every field
   unconditionally. */
export function FieldError({ field, errors, id }) {
  const message = errors?.[field];
  if (!message) return null;
  return (
    <p id={id || `${field}-err`} role="alert" aria-live="polite" className="mt-1.5 text-xs font-medium text-red-700">
      {message}
    </p>
  );
}

/* A server refusal, split for the screen (session 6c, WCAG 3.3.1).

   DRF answers a bad field with `{field: ["message"]}`. The forms used to join
   every key into one toast ("password: This password is too common. • email:
   ..."), which names fields by their API key and vanishes after four seconds.
   This returns the messages for the fields the form can show (`fields`), keyed
   for `showFieldErrors`, and everything else as one sentence for the toast.

   `fields` may map an API key to the form's own field name:
   `{ email: "email", password1: "password" }`, or be a plain array. */
export function serverFieldErrors(data, fields) {
  const map = {};
  const rest = [];
  const known = Array.isArray(fields)
    ? Object.fromEntries(fields.map((f) => [f, f]))
    : fields || {};
  const text = (v) => {
    if (v == null) return "";
    if (Array.isArray(v)) return v.map(text).filter(Boolean).join(" ");
    if (typeof v === "object") return Object.values(v).map(text).filter(Boolean).join(" ");
    return String(v);
  };
  if (data && typeof data === "object" && !Array.isArray(data)) {
    for (const [key, value] of Object.entries(data)) {
      const msg = text(value);
      if (!msg) continue;
      if (known[key]) map[known[key]] = msg;
      else rest.push(msg);
    }
  } else if (data) {
    rest.push(text(data));
  }
  return { map, rest: rest.join(" ") };
}
